import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Heart, 
  ArrowLeft,
  X,
  Building2,
  MapPin,
  DollarSign,
  Clock,
  ExternalLink,
  Search,
  SlidersHorizontal,
  RefreshCw,
  Calendar,
  Briefcase,
  Bookmark,
  User,
  Filter,
  ChevronDown,
  BarChart4,
  Star
} from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { Job } from "@/types/job";
import { StoredJobs } from "@/hooks/useJobSwipe";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const LikedJobs = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [likedJobs, setLikedJobs] = useState<Job[]>([]);
  const [dislikedJobs, setDislikedJobs] = useState<Job[]>([]);
  const [sortBy, setSortBy] = useState("recent");
  const [savedJobs, setSavedJobs] = useState<Job[]>([]);

  useEffect(() => {
    const fetchJobs = () => {
      const storedJobs = localStorage.getItem("dgk-jobs");
      if (storedJobs) {
        const parsedJobs: StoredJobs = JSON.parse(storedJobs);
        setLikedJobs(parsedJobs.likedJobs || []);
        setDislikedJobs(parsedJobs.dislikedJobs || []);
      }
      setIsLoading(false);
    };

    setTimeout(() => {
      fetchJobs();
    }, 800);
  }, []);

  const handleRemoveFromLiked = (jobId: number) => {
    const updatedLikedJobs = likedJobs.filter(job => job.id !== jobId);
    setLikedJobs(updatedLikedJobs);
    
    const storedJobs = localStorage.getItem("dgk-jobs");
    if (storedJobs) {
      const parsedJobs: StoredJobs = JSON.parse(storedJobs);
      
      localStorage.setItem("dgk-jobs", JSON.stringify({
        ...parsedJobs,
        likedJobs: updatedLikedJobs
      }));
    }
    
    toast({
      description: "Job removed from your liked jobs",
    });
  };

  const handleRemoveFromDisliked = (jobId: number) => {
    const updatedDislikedJobs = dislikedJobs.filter(job => job.id !== jobId);
    setDislikedJobs(updatedDislikedJobs);
    
    const storedJobs = localStorage.getItem("dgk-jobs");
    if (storedJobs) {
      const parsedJobs: StoredJobs = JSON.parse(storedJobs);
      
      localStorage.setItem("dgk-jobs", JSON.stringify({
        ...parsedJobs,
        dislikedJobs: updatedDislikedJobs
      }));
    }
    
    toast({
      description: "Job removed from your disliked jobs",
    });
  };

  const handleReconsiderJob = (job: Job) => {
    const updatedDislikedJobs = dislikedJobs.filter(j => j.id !== job.id);
    setDislikedJobs(updatedDislikedJobs);
    
    const updatedLikedJobs = [...likedJobs, job];
    setLikedJobs(updatedLikedJobs);
    
    localStorage.setItem("dgk-jobs", JSON.stringify({
      likedJobs: updatedLikedJobs,
      dislikedJobs: updatedDislikedJobs,
      viewedJobIds: [...updatedLikedJobs, ...updatedDislikedJobs].map(j => 
        typeof j.id === 'string' ? parseInt(j.id) : j.id
      )
    }));
    
    toast({
      title: "Job Reconsidered",
      description: "Job moved to your liked jobs",
    });
  };

  const handleSaveJob = (job: Job) => {
    setSavedJobs(prev => [...prev, job]);
    toast({
      title: "Job Saved",
      description: "You can find this job in your saved items",
    });
  };

  const handleApplyJob = (jobId: number) => {
    toast({
      title: "Application Submitted!",
      description: "Your application has been submitted successfully",
      variant: "success",
    });
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      const storedJobs = localStorage.getItem("dgk-jobs");
      if (storedJobs) {
        const parsedJobs: StoredJobs = JSON.parse(storedJobs);
        setLikedJobs(parsedJobs.likedJobs || []);
        setDislikedJobs(parsedJobs.dislikedJobs || []);
      }
      setIsLoading(false);
      toast({
        description: "Job history refreshed",
      });
    }, 800);
  };

  const filteredLikedJobs = likedJobs.filter(job => 
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDislikedJobs = dislikedJobs.filter(job => 
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortJobs = (jobs: Job[]) => {
    switch (sortBy) {
      case "recent":
        return [...jobs];
      case "salary-high":
        return [...jobs].sort((a, b) => {
          const aSalary = parseInt(String(a.salary).split("$")[2]?.replace(",", "") || "0");
          const bSalary = parseInt(String(b.salary).split("$")[2]?.replace(",", "") || "0");
          return bSalary - aSalary;
        });
      case "salary-low":
        return [...jobs].sort((a, b) => {
          const aSalary = parseInt(String(a.salary).split("$")[1]?.split(" ")[0].replace(",", "") || "0");
          const bSalary = parseInt(String(b.salary).split("$")[1]?.split(" ")[0].replace(",", "") || "0");
          return aSalary - bSalary;
        });
      case "alphabetical":
        return [...jobs].sort((a, b) => a.title.localeCompare(b.title));
      default:
        return [...jobs];
    }
  };

  const sortedLikedJobs = sortJobs(filteredLikedJobs);
  const sortedDislikedJobs = sortJobs(filteredDislikedJobs);

  return (
    <div className="min-h-screen p-4 bg-gradient-to-b from-blue-50/50 via-white to-emerald-50/30 animate-fade-in">
      <div className="max-w-7xl mx-auto pt-8">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/job-swipe">
              <Button variant="ghost" size="icon" className="hover:bg-blue-50">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-600 to-emerald-600 text-transparent bg-clip-text">
                Your Job History
              </h1>
              <p className="text-gray-600 mt-1">Track and manage your job applications</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              className="flex items-center gap-2" 
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600">
              <Link to="/job-swipe" className="flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Continue Swiping
              </Link>
            </Button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search jobs by title, company, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>Sort by</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="salary-high">Highest Salary</SelectItem>
                <SelectItem value="salary-low">Lowest Salary</SelectItem>
                <SelectItem value="alphabetical">Alphabetical</SelectItem>
              </SelectContent>
            </Select>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter Options
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuItem onClick={() => setFilter("all")}>
                  All Jobs
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter("remote")}>
                  Remote Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter("fulltime")}>
                  Full-time Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter("contract")}>
                  Contract Only
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <Card className="h-min p-6 col-span-1">
            <h3 className="text-lg font-semibold mb-4">Job History Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="bg-emerald-100 p-2 rounded-full">
                    <Heart className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span>Liked Jobs</span>
                </div>
                <span className="font-semibold">{likedJobs.length}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-rose-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="bg-rose-100 p-2 rounded-full">
                    <X className="h-4 w-4 text-rose-600" />
                  </div>
                  <span>Passed Jobs</span>
                </div>
                <span className="font-semibold">{dislikedJobs.length}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Star className="h-4 w-4 text-amber-600" />
                  </div>
                  <span>Saved Jobs</span>
                </div>
                <span className="font-semibold">{savedJobs.length}</span>
              </div>
              
              <Button className="w-full mt-2" asChild>
                <Link to="/projects">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Browse Projects
                </Link>
              </Button>
            </div>
          </Card>
          
          <div className="col-span-1 lg:col-span-3">
            <Tabs defaultValue="liked" className="w-full">
              <TabsList className="w-full mb-6">
                <TabsTrigger value="liked" className="w-1/2">
                  <Heart className="w-4 h-4 mr-2" />
                  Liked Jobs ({filteredLikedJobs.length})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="w-1/2">
                  <X className="w-4 h-4 mr-2" />
                  Passed Jobs ({filteredDislikedJobs.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="liked">
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
                  </div>
                ) : sortedLikedJobs.length > 0 ? (
                  <motion.div
                    variants={container}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                  >
                    {sortedLikedJobs.map((job) => (
                      <motion.div key={job.id} variants={item}>
                        <Card className="p-4 hover:shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm border-emerald-100">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-sky-100 to-emerald-100 flex items-center justify-center">
                              {job.logo ? (
                                <img src={job.logo} alt={job.company} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <Building2 className="w-6 h-6 text-emerald-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{job.title}</h3>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <Building2 className="w-3 h-3" />
                                <span>{job.company}</span>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <div className="flex items-center gap-1 text-gray-500 text-sm">
                                  <MapPin className="w-3 h-3" />
                                  <span>{job.location}</span>
                                </div>
                                <div className="flex items-center gap-1 text-sky-600 text-sm">
                                  <DollarSign className="w-3 h-3" />
                                  <span>{job.salary}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="secondary" className={
                                  job.type === "Full-time" ? "bg-blue-100 text-blue-800" :
                                  job.type === "Part-time" ? "bg-purple-100 text-purple-800" :
                                  job.type === "Contract" ? "bg-amber-100 text-amber-800" :
                                  "bg-gray-100 text-gray-800"
                                }>
                                  {job.type}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {job.experience || "All Levels"}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {job.skills.slice(0, 3).map((skill, index) => (
                                  <Badge 
                                    key={index} 
                                    variant="outline" 
                                    className="bg-sky-50 text-sky-700 border-sky-100 text-xs"
                                  >
                                    {skill}
                                  </Badge>
                                ))}
                                {job.skills.length > 3 && (
                                  <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-100 text-xs">
                                    +{job.skills.length - 3} more
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-center gap-1 text-gray-500 text-xs">
                                  <Clock className="w-3 h-3" />
                                  <span>{new Date(job.postedDate || job.posted).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                                    onClick={() => handleRemoveFromLiked(Number(job.id))}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Remove
                                  </Button>
                                  <Button 
                                    variant="default" 
                                    size="sm" 
                                    className="h-8"
                                    onClick={() => handleApplyJob(Number(job.id))}
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Apply
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-12 glass-effect rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No liked jobs yet</h3>
                    <p className="text-gray-600 mb-6">Start swiping to find projects that match your skills</p>
                    <Button asChild>
                      <Link to="/job-swipe">
                        Go to Job Swipe
                      </Link>
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="rejected">
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
                  </div>
                ) : sortedDislikedJobs.length > 0 ? (
                  <motion.div
                    variants={container}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                  >
                    {sortedDislikedJobs.map((job) => (
                      <motion.div key={job.id} variants={item}>
                        <Card className="p-4 hover:shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm opacity-75">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                              {job.logo ? (
                                <img src={job.logo} alt={job.company} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <Building2 className="w-6 h-6 text-gray-500" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-700">{job.title}</h3>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <Building2 className="w-3 h-3" />
                                <span>{job.company}</span>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <div className="flex items-center gap-1 text-gray-500 text-sm">
                                  <MapPin className="w-3 h-3" />
                                  <span>{job.location}</span>
                                </div>
                                <div className="flex items-center gap-1 text-gray-600 text-sm">
                                  <DollarSign className="w-3 h-3" />
                                  <span>{job.salary}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700">
                                  {job.type}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {job.experience || "All Levels"}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-center gap-1 text-gray-500 text-xs">
                                  <Clock className="w-3 h-3" />
                                  <span>{new Date(job.postedDate || job.posted).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8"
                                    onClick={() => handleRemoveFromDisliked(Number(job.id))}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Remove
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 border-emerald-200"
                                    onClick={() => handleReconsiderJob(job)}
                                  >
                                    <Heart className="w-3 h-3 mr-1" />
                                    Reconsider
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-12 glass-effect rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No passed jobs yet</h3>
                    <p className="text-gray-600">Jobs you pass on will appear here</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LikedJobs;
